import React, { useState, useMemo } from 'react';
import { FiEye, FiX, FiCalendar, FiMapPin, FiClock, FiUser, FiMail, FiBookOpen, FiBriefcase } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { useEventRequests, useEventRequest } from '../hooks/useEventRequests';

// Event Request Details Modal Component
const EventRequestDetailsModal = ({ isOpen, onClose, eventRequestId }) => {
  const { data: eventRequestResponse, isLoading } = useEventRequest(eventRequestId);
  console.log(eventRequestResponse);

  if (!isOpen || !eventRequestId) return null;

  const eventRequest = eventRequestResponse?.data;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Event Request Details</h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        ) : eventRequest ? (
          <div className="space-y-6">
            {/* Student Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FiUser className="mr-2" />
                Student Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-gray-900">{eventRequest.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{eventRequest.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Major</label>
                  <p className="text-gray-900">{eventRequest.major}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">National ID</label>
                  <p className="text-gray-900">{eventRequest.national_id || 'N/A'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <p className="text-gray-900">{eventRequest.message || 'No message'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    eventRequest.status === 'accepted' ? 'bg-green-100 text-green-800' :
                    eventRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {eventRequest.status_label}
                  </span>
                </div>
              </div>
            </div>

            {/* Event Information */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FiCalendar className="mr-2" />
                Event Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Event Title</label>
                  <p className="text-gray-900">{eventRequest.event.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Event State</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    eventRequest.event.state === 'completed' ? 'bg-green-100 text-green-800' :
                    eventRequest.event.state === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {eventRequest.event.state_label}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <p className="text-gray-900 flex items-center">
                    <FiMapPin className="mr-1" />
                    {eventRequest.event.location}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Time</label>
                  <p className="text-gray-900 flex items-center">
                    <FiClock className="mr-1" />
                    {eventRequest.event.time}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <p className="text-gray-900">{eventRequest.event.start_date}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <p className="text-gray-900">{eventRequest.event.end_date}</p>
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FiBriefcase className="mr-2" />
                Company Information
              </h4>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {eventRequest.event.company.image ? (
                    <img 
                      src={eventRequest.event.company.image} 
                      alt={eventRequest.event.company.name}
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center">
                      <FiBriefcase className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h5 className="text-lg font-medium text-gray-900">{eventRequest.event.company.name}</h5>
                  <p className="text-gray-600">{eventRequest.event.company.email}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            Event request not found
          </div>
        )}
      </div>
    </div>
  );
};

const EventRequests = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEventRequest, setSelectedEventRequest] = useState(null);

  // Fetch event requests using React Query
  const {
    data: eventRequestsResponse,
    isLoading,
    error,
    isFetching,
  } = useEventRequests(pagination.current, pagination.pageSize);

  console.log("eventRequestsResponse",eventRequestsResponse);

  // Memoized event requests data
  const { eventRequestsData, totalRecords } = useMemo(() => {
    if (!eventRequestsResponse) {
      return { eventRequestsData: [], totalRecords: 0 };
    }

    return {
      eventRequestsData: eventRequestsResponse.data.data || [],
      totalRecords: eventRequestsResponse.total || 0
    };
  }, [eventRequestsResponse]);

  // Get error message from query
  const errorMessage = error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      title: 'Student Name',
      sortable: true,
    },
    {
      key: 'email',
      title: 'Email',
      sortable: true,
    },
    {
      key: 'major',
      title: 'Major',
      sortable: true,
    },
    {
      key: 'status',
      title: 'Status',
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'accepted' ? 'bg-green-100 text-green-800' :
          value === 'rejected' ? 'bg-red-100 text-red-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {row.status_label}
        </span>
      ),
    },
    {
      key: 'event',
      title: 'Event',
      render: (value) => (
        <div className="space-y-1">
          <div className="font-medium text-gray-900">{value.title}</div>
          <div className="text-sm text-gray-500 flex items-center">
            <FiMapPin className="mr-1 h-3 w-3" />
            {value.location}
          </div>
          <div className="text-sm text-gray-500 flex items-center">
            <FiCalendar className="mr-1 h-3 w-3" />
            {value.start_date}
          </div>
        </div>
      ),
    },
    {
      key: 'event',
      title: 'Company',
      render: (value) => (
        <div className="flex items-center space-x-2">
          {value.company.image ? (
            <img 
              src={value.company.image} 
              alt={value.company.name} 
              className="h-8 w-8 rounded object-cover"
            />
          ) : (
            <div className="h-8 w-8 bg-gray-200 rounded flex items-center justify-center">
              <FiBriefcase className="h-4 w-4 text-gray-500" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">{value.company.name}</div>
            <div className="text-sm text-gray-500">{value.company.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
          >
            <FiEye className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Event Requests', href: '/dashboard/event-requests' },
  ];

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination(prev => ({ ...prev, pageSize, current }));
  };

  const handleViewDetails = (eventRequest) => {
    setSelectedEventRequest(eventRequest);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedEventRequest(null);
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user}>
        <div className="space-y-0">
          <PageHeader
            title="Event Requests"
            description="Manage event requests from students"
            breadcrumbs={breadcrumbs}
            user={user}
          />
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Loading event requests...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="Event Requests"
          description="Manage event requests from students"
          breadcrumbs={breadcrumbs}
        />

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="text-red-800">{errorMessage}</div>
          </div>
        )}

        <DataTable
          data={eventRequestsData}
          columns={columns}
          loading={isLoading}
          filterable={false}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalRecords,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyMessage="No event requests found"
        />

        {/* Event Request Details Modal */}
        <EventRequestDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          eventRequestId={selectedEventRequest?.id}
        />
      </div>
    </Layout>
  );
};

export default EventRequests;
